import { gql, useMutation } from '@apollo/client';
import { yupResolver } from '@hookform/resolvers/yup';
import { Checkbox } from 'components/Checkbox/Checkbox';
import { CountryPhoneSelect } from 'components/CountrySelcet/CountryPhoneSelect';
import { ErrorMesagge } from 'components/ErrorMesagge/ErrorMesagge';
import { Textarea } from 'components/Textarea/Textarea';
import { TextInput } from 'components/TextInput/TextInput';
import { Label } from 'components/TextInput/TextInput.styled';
import { notify } from 'components/ToastNotification/ToastNotification';
import React from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { ToastTypes } from 'types/CommonTypes';
import {
    ButtonFormWrapper,
    ContactTitle,
    ErrorMesaggeWrapper,
    FormWrapper,
    InputWrapper,
    PhoneWrapper,
    Privacy,
    PrivacyInfo,
    StyledButton,
} from './Form.styled';
import { validation } from './validation';

interface FormProps {
    isModal?: boolean;
    inputIsWhite?: boolean;
}

const CONTACT_MUTATION = gql`
    mutation ContactForm($input: SendContactFormInput!) {
        sendContactForm(input: $input) {
            clientMutationId
            data
            success
        }
    }
`;

const isBrowser = typeof window !== 'undefined';

export const Form = ({ isModal, inputIsWhite }: FormProps) => {
    const flatFromUrl =
        isBrowser &&
        window.location.pathname.replace('/mieszkanie/', '').split('/');

    const {
        control,
        handleSubmit,
        formState: { errors, isDirty, isValid },
        reset,
        watch,
    } = useForm<FieldValues>({
        defaultValues: {
            message: isModal
                ? //@ts-ignore
                  `Dzień dobry, interesuje mnie mieszkanie № ${flatFromUrl[1]} w budynku ${flatFromUrl[0]}. Proszę o kontakt.`
                : undefined,
        },
        mode: 'all',
        resolver: yupResolver(validation),
    });
    const form = watch();

    const isError =
        errors.name || errors.phone || errors.email || errors.privacy;

    const [contactMutation] = useMutation(CONTACT_MUTATION, {
        variables: {
            input: {
                //@ts-ignore
                buildingName: flatFromUrl[0] ? flatFromUrl[0] : '',
                emailAddress: form?.email ? form.email : '',
                firstAndLastName: form?.name ? form.name : '',
                floorNumber: '',
                investmentName: 'Kuźnicy Kołłątajowskiej 68',
                //@ts-ignore
                investmentNumber: flatFromUrl[1] ? flatFromUrl[1] : '',
                investmentType: 'Mieszkanie',
                messageText: form?.message ? form.message : '',
                phoneNumber:
                    form?.country?.phone && form.phone
                        ? form.country.phone + form.phone
                        : '',
                investmentSlug: 'kuznicy-kollatajowskiej-68',
                clientMutationId: 'internal-id',
            },
        },
    });

    const onSubmit = () => {
        const contactSubmission = contactMutation();
        contactSubmission.then(() => {
            reset({ name: '', phone: '', email: '', message: '' });
            return notify('Wiadomość została wysłana.')[ToastTypes.SUCCESS]();
        });
        contactSubmission.catch(() => {
            return notify('Wystąpił nieoczkiwany błąd, spróbuj ponownie.')[
                ToastTypes.ERROR
            ]();
        });
    };

    return (
        <FormWrapper onSubmit={handleSubmit(onSubmit)}>
            {isModal && <ContactTitle>Skontaktuj się z nami</ContactTitle>}
            <TextInput
                control={control}
                errors={errors.name}
                inputIsWhite={inputIsWhite}
                label="Imię i nazwisko"
                name="name"
                placeholder="Wpisz swoje imię i nazwisko"
                type="text"
            />
            <PhoneWrapper>
                <Label>
                    Telefon kontaktowy
                    <InputWrapper>
                        <CountryPhoneSelect
                            control={control}
                            inputIsWhite={inputIsWhite}
                        />
                        <TextInput
                            control={control}
                            inputIsWhite={inputIsWhite}
                            name="phone"
                            type="text"
                        />
                    </InputWrapper>
                </Label>
                {errors.phone && <ErrorMesagge error={errors.phone} />}
            </PhoneWrapper>

            <TextInput
                control={control}
                errors={errors.email}
                inputIsWhite={inputIsWhite}
                label="Adres e-mail"
                name="email"
                placeholder="Wpisz swój adres e-mail"
                type="email"
            />
            <Textarea
                control={control}
                label="Wiadomość (opcjonalnie)"
                placeholder={
                    isModal
                        ? 'Dzień dobry, interesuje mnie mieszkanie № 124 w budynku A. Proszę o kontakt.'
                        : 'Wpisz wiadomość'
                }
                inputIsWhite={inputIsWhite}
                name="message"
            />
            <>
                <>
                    <Privacy>
                        <Checkbox
                            name="privacy"
                            control={control}
                            isError={!!errors.privacy}
                        />

                        <PrivacyInfo>
                            Wyrażam zgodę na przetwarzanie podanych przeze mnie
                            danych osobowych. Administratorem danych jest Real
                            Construct Sp. z o.o., posiadająca siedzibę w
                            Krakowie (31-231) przy Siewnej 44. Mam świadomość
                            prawa dostępu do swoich danych i ich poprawiania
                            oraz wystąpienia o zaprzestanie przetwarzania
                            podanych przeze mnie danych osobowych. Podanie
                            danych jest dobrowolne.
                        </PrivacyInfo>
                    </Privacy>
                    {errors.privacy && (
                        <ErrorMesaggeWrapper>
                            <ErrorMesagge error={errors.privacy} />
                        </ErrorMesaggeWrapper>
                    )}
                </>
                <>
                    <Privacy>
                        <Checkbox
                            name="contact"
                            control={control}
                            isError={!!errors.contact}
                        />

                        <PrivacyInfo>
                            Wyrażam zgodę na otrzymywanie od Real Construct Sp.
                            z o.o. za pomocą telefonu i/lub środków komunikacji
                            elektronicznej, skierowanej do mnie informacji
                            handlowej w rozumieniu ustawy z dnia 18 lipca 2002
                            r. o świadczeniu usług drogą elektroniczną oraz
                            wyrażam zgodę na przesyłanie mi ofert/ogłoszeń
                            nieruchomości i usług przez Real Construct Sp. z
                            o.o.
                        </PrivacyInfo>
                    </Privacy>
                    {errors.contact && (
                        <ErrorMesaggeWrapper>
                            <ErrorMesagge error={errors.contact} />
                        </ErrorMesaggeWrapper>
                    )}
                </>
            </>
            <ButtonFormWrapper isModal={isModal}>
                <StyledButton
                    as="button"
                    disabled={!!isError || !isValid || !isDirty}
                >
                    Wyślij wiadomość
                </StyledButton>
            </ButtonFormWrapper>
        </FormWrapper>
    );
};
